import React, { useEffect, useState } from "react";
import { api, dateFormat, formatApiErrorDetail } from "../../lib/api";
import ExportButton from "../../components/ExportButton";
import { useAuth } from "../../context/AuthContext";
import { Plus, X, Pencil, KeyRound, ShieldAlert } from "lucide-react";

const ROLES = [
  { value: "kunde", label: "Kunde" },
  { value: "vertriebler", label: "Vertriebler" },
  { value: "admin", label: "Admin" },
  { value: "buchhalter", label: "Buchhalter" },
];

const MODI = [
  { value: "privat", label: "Privat (FalkeCoins)" },
  { value: "gewerblich_aktiv", label: "Gewerblich (2 % Provision)" },
  { value: "gewerblich_inaktiv", label: "Gewerblich (inaktiv)" },
];

const blank = () => ({ name: "", email: "", password: "", role: "kunde", tippgeber_modus: "privat", telefon: "", anlagenadresse: "" });

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(blank());
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/users").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const filtered = items.filter((u) => {
    if (roleFilter && u.role !== roleFilter) return false;
    if (!filter) return true;
    const q = filter.toLowerCase();
    return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
  });

  const korrektur = async (uid) => {
    const c = prompt("Coin-Korrektur (positiv/negativ):");
    if (!c) return;
    const b = prompt("Beschreibung:") || "Manuelle Korrektur";
    const coins = Number(c);
    let alsEmpfehlung = false;
    if (coins > 0) {
      alsEmpfehlung = window.confirm(
        "Soll diese Coin-Gutschrift auch als +1 Empfehlung im Stufen-Fortschritt zählen?\n\n" +
        "OK = ja, Stufe & Lifetime hochzählen\nAbbrechen = nur Coins, ohne Stufenfortschritt"
      );
    }
    try {
      await api.post(`/admin/users/${uid}/coin-korrektur`, { coins, beschreibung: b, als_empfehlung_zaehlen: alsEmpfehlung });
      load();
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || "Fehler"); }
  };

  const createUser = async () => {
    setError(""); setBusy(true);
    try {
      await api.post("/admin/users", form);
      setShowCreate(false); setForm(blank()); load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Fehler beim Anlegen");
    } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    setError(""); setBusy(true);
    try {
      const payload = { ...editForm };
      // Nur geänderte Felder senden
      Object.keys(payload).forEach((k) => { if (payload[k] === editing[k]) delete payload[k]; });
      if (Object.keys(payload).length === 0) { setEditing(null); return; }
      await api.put(`/admin/users/${editing.id}`, payload);
      setEditing(null); load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Fehler beim Speichern");
    } finally { setBusy(false); }
  };

  const resetPw = async (u) => {
    const np = prompt(`Neues Passwort für ${u.email} (min. 8 Zeichen):`);
    if (!np || np.length < 8) return alert("Mindestens 8 Zeichen!");
    try {
      await api.post(`/admin/users/${u.id}/passwort-zuruecksetzen`, { password: np });
      alert(`Passwort für ${u.email} wurde gesetzt.`);
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail) || "Fehler");
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-users-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-heading font-bold text-falke-blue">Nutzer-Verwaltung</h1>
        <div className="flex flex-wrap gap-2">
          <ExportButton endpoint="/admin/exports/coin-konten" defaultFilename="FalkeCoins_Konten.csv" label="FalkeCoin-Konten exportieren" withPeriod={false} testId="export-konten" />
          <button onClick={() => { setShowCreate(true); setForm(blank()); setError(""); }} className="btn-primary inline-flex items-center gap-2" data-testid="btn-new-user">
            <Plus size={16} /> Neuer Nutzer
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Name oder E-Mail…" className="px-3 py-2 rounded-xl border border-falke-blue/15 flex-1 min-w-[200px] max-w-md" data-testid="user-search" />
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="px-3 py-2 rounded-xl border border-falke-blue/15" data-testid="user-role-filter">
          <option value="">Alle Rollen</option>
          {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </div>

      <div className="falke-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-falke-blue/70 text-xs uppercase tracking-wider border-b border-falke-blue/10">
            <tr><th className="px-4 py-3">Name</th><th>E-Mail</th><th>Rolle</th><th>Modus</th><th>Stufe</th><th className="text-right">Coins</th><th>Reg.</th><th>Aktion</th></tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} className={`border-t border-falke-blue/5 ${u.aktiv === false ? "opacity-50" : ""}`} data-testid={`user-${u.id}`}>
                <td className="px-4 py-3 font-medium">{u.name}{u.id === me.id && <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-falke-orange/20 text-falke-orange">Du</span>}</td>
                <td className="text-falke-anthracite/80">{u.email}</td>
                <td><span className={`status-badge ${u.role === "admin" ? "bg-falke-orange/15 text-falke-orange" : "bg-falke-blue/10 text-falke-blue"}`}>{u.role}</span></td>
                <td className="text-xs">{u.tippgeber_modus || "—"}</td>
                <td className="text-xs">{u.aktuelle_stufe || "—"}</td>
                <td className="tabular text-right pr-4">{u.verfuegbare_coins ?? "—"}</td>
                <td className="text-xs whitespace-nowrap">{dateFormat(u.erstellt_am)}</td>
                <td className="whitespace-nowrap py-3 pr-3">
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
                    <button onClick={() => { setEditing(u); setEditForm({ name: u.name, role: u.role, tippgeber_modus: u.tippgeber_modus, telefon: u.telefon || "", anlagenadresse: u.anlagenadresse || "", aktiv: u.aktiv !== false }); setError(""); }} className="text-falke-blue hover:text-falke-orange inline-flex items-center gap-1" data-testid={`edit-${u.id}`}><Pencil size={12} /> Bearbeiten</button>
                    <button onClick={() => resetPw(u)} className="text-falke-blue hover:text-falke-orange inline-flex items-center gap-1" data-testid={`pw-${u.id}`}><KeyRound size={12} /> Passwort</button>
                    {u.role === "kunde" && <button onClick={() => korrektur(u.id)} className="text-falke-orange font-semibold" data-testid={`korrektur-${u.id}`}>Coin-Korrektur</button>}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-falke-anthracite/50">Keine Nutzer gefunden</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="create-user-modal">
          <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-3 mb-4">
              <h3 className="font-heading text-2xl text-falke-blue inline-flex items-center gap-2"><Plus size={20} /> Neuer Nutzer</h3>
              <button onClick={() => setShowCreate(false)} className="text-falke-blue/60 text-2xl leading-none" data-testid="cu-close">×</button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-falke-blue mb-1">Name *</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-falke-blue/15" data-testid="cu-name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-falke-blue mb-1">E-Mail *</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-falke-blue/15" data-testid="cu-email" />
              </div>
              <div>
                <label className="block text-sm font-medium text-falke-blue mb-1">Passwort * (min. 8 Zeichen)</label>
                <input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-falke-blue/15 font-mono" placeholder="z. B. FalkeStart2026!" data-testid="cu-pw" />
              </div>
              <div>
                <label className="block text-sm font-medium text-falke-blue mb-1">Rolle *</label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-falke-blue/15" data-testid="cu-role">
                  {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              {form.role === "kunde" && (
                <div>
                  <label className="block text-sm font-medium text-falke-blue mb-1">Tippgeber-Modus</label>
                  <select value={form.tippgeber_modus} onChange={(e) => setForm({ ...form, tippgeber_modus: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-falke-blue/15" data-testid="cu-modus">
                    {MODI.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-falke-blue mb-1">Telefon</label>
                  <input value={form.telefon} onChange={(e) => setForm({ ...form, telefon: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-falke-blue/15" data-testid="cu-tel" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-falke-blue mb-1">Anlagenadresse</label>
                  <input value={form.anlagenadresse} onChange={(e) => setForm({ ...form, anlagenadresse: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-falke-blue/15" data-testid="cu-addr" />
                </div>
              </div>
              {form.role === "admin" && (
                <div className="p-3 rounded-xl bg-falke-orange/10 border border-falke-orange/30 text-xs text-falke-anthracite inline-flex gap-2">
                  <ShieldAlert size={16} className="text-falke-orange flex-shrink-0" />
                  <span>Admins haben vollen Zugriff auf alle Funktionen inkl. Provisionen, Gutschriften und Nutzer-Verwaltung.</span>
                </div>
              )}
              {error && <div className="text-sm text-rose-600" data-testid="cu-error">{error}</div>}
              <button onClick={createUser} disabled={busy || !form.name || !form.email || !form.password} className="btn-primary w-full" data-testid="cu-submit">
                {busy ? "Anlegen..." : "Nutzer anlegen"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="edit-user-modal">
          <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-3 mb-4">
              <h3 className="font-heading text-xl text-falke-blue inline-flex items-center gap-2"><Pencil size={18} /> {editing.name}</h3>
              <button onClick={() => setEditing(null)} className="text-falke-blue/60 text-2xl leading-none" data-testid="eu-close">×</button>
            </div>
            <p className="text-xs text-falke-anthracite/60 mb-4">{editing.email}</p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-falke-blue mb-1">Name</label>
                <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-falke-blue/15" data-testid="eu-name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-falke-blue mb-1">Rolle</label>
                <select value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-falke-blue/15" data-testid="eu-role">
                  {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              {editForm.role === "kunde" && (
                <div>
                  <label className="block text-sm font-medium text-falke-blue mb-1">Modus</label>
                  <select value={editForm.tippgeber_modus || "privat"} onChange={(e) => setEditForm({ ...editForm, tippgeber_modus: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-falke-blue/15" data-testid="eu-modus">
                    {MODI.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-falke-blue mb-1">Telefon</label>
                  <input value={editForm.telefon || ""} onChange={(e) => setEditForm({ ...editForm, telefon: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-falke-blue/15" data-testid="eu-tel" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-falke-blue mb-1">Anlagenadresse</label>
                  <input value={editForm.anlagenadresse || ""} onChange={(e) => setEditForm({ ...editForm, anlagenadresse: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-falke-blue/15" data-testid="eu-addr" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm pt-2">
                <input type="checkbox" checked={editForm.aktiv !== false} onChange={(e) => setEditForm({ ...editForm, aktiv: e.target.checked })} className="w-4 h-4 accent-falke-orange" data-testid="eu-aktiv" />
                Account aktiv (Login möglich)
              </label>
              {error && <div className="text-sm text-rose-600" data-testid="eu-error">{error}</div>}
              <button onClick={saveEdit} disabled={busy} className="btn-primary w-full" data-testid="eu-submit">{busy ? "Speichern…" : "Änderungen speichern"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
